import { Dialog, DialogActions, DialogContent, Icon, IconButton } from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDButton from "components/Basics/MDButton";
import MDTypography from "components/Basics/MDTypography";
import { getLabel, getDictionaryPath } from "helpers/dictionary";
import { t } from "i18next";
import lod_ from "lodash";
import DictionaryMenu from "pages/settings/filters/DictionaryMenu";
import { useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useSelector } from "react-redux";
import { selectCurrentProfile } from "redux-react/reducers/profileReducer";

/**
 * Reorder columns dialog component
 * Allows users to reorder columns in a table by dragging and dropping.
 * It also allows users to add new columns from a dictionary.
 * @param {Object} props - The component props.
 * @param {Object} props.reorderDialog - The state of the reorder dialog.
 * @param {Function} props.setReorderDialog - Function to update the state of the reorder dialog.
 * @param {Array} props.header - The table header columns.
 * @param {Array} props.attributeToDisplay - The attributes to display in the table.
 * @param {string} props.collection - The collection name for the dictionary.
 * @param {Object} props.dictionary - The dictionary object containing labels and items.
 * @returns {JSX.Element} The rendered dialog component.
 */
const ReorderColumnsDialog = ({
	reorderDialog,
	setReorderDialog,
	header = [],
	attributeToDisplay = [],
	collection,
	dictionary = {},
	onSave = () => {}
}) => {
	const profile = useSelector(selectCurrentProfile);
	const language = profile?.language || "fr";

	const [achorEl, setAnchorEl] = useState(null);

	const filteredHeader = header.filter(item => attributeToDisplay.includes(item.accessor));

	const [temporaryValues, setTemporaryValues] = useState(
		filteredHeader.map(item => {
			const code = item.accessor;
			const dictionaryItem = lod_.get(dictionary[collection]?.items ?? {}, getDictionaryPath(code));
			const label = getLabel(dictionaryItem?.label ?? {}, language);
			return { code, label: label ?? code };
		})
	);

	const moveItem = (fromIndex, toIndex) => {
		const newItems = [...temporaryValues];
		const [movedItem] = newItems.splice(fromIndex, 1);
		newItems.splice(toIndex, 0, movedItem);
		setTemporaryValues(newItems);
	};

	const handleDragEnd = result => {
		if (!result.destination) {
			return;
		}

		const sourceIndex = result.source.index;
		const destinationIndex = result.destination.index;

		if (sourceIndex !== destinationIndex) {
			moveItem(sourceIndex, destinationIndex);
		}
	};

	const removeItem = index => {
		const newItems = temporaryValues.filter((_, i) => i !== index);
		setTemporaryValues(newItems);
	};

	const addItem = path => {
		const item = lod_.get(dictionary, getDictionaryPath(path));
		const code = item?.code;
		const label = getLabel(item?.label ?? {}, language) || code;

		// Split path "." and remove first one
		const queryPath = path.split(".").slice(1).join(".");

		// Check if item already exists
		const exists = temporaryValues.find(item => item.code === queryPath);
		if (!exists) {
			setTemporaryValues([...temporaryValues, { code: queryPath, label }]);
		}
		setAnchorEl(null);
	};

	return (
		<Dialog fullWidth maxWidth="xl" open={reorderDialog.open}>
			<DialogContent>
				<MDBox>
					{/* Reorderable columns display */}
					<MDBox>
						<MDBox
							display="flex"
							flexDirection="row"
							justifyContent="space-between"
							alignItems="center"
							sx={{ mb: 1 }}
						>
							<MDTypography variant="h6">{t("DATATABLE.columnsToDisplayTitle")}</MDTypography>
							{/* Add column */}
							<MDBox>
								<MDButton variant="gradient" color="info" onClick={e => setAnchorEl(e.target)}>
									{t("SETTINGS.add")}
								</MDButton>
								<DictionaryMenu
									placement="right"
									dictionary={dictionary}
									clickableChildsOfLevels={false}
									anchorEl={achorEl}
									handleInsertText={addItem}
									handleClose={() => setAnchorEl(null)}
								/>
							</MDBox>
						</MDBox>
						<DragDropContext onDragEnd={handleDragEnd}>
							<Droppable droppableId="columns" direction="horizontal">
								{(provided, snapshot) => (
									<MDBox
										{...provided.droppableProps}
										ref={provided.innerRef}
										display="flex"
										flexDirection="row"
										flexWrap="wrap"
										gap={1}
										p={1}
										border="1px solid #e0e0e0"
										borderRadius="8px"
										minHeight="60px"
										sx={{
											backgroundColor: snapshot.isDraggingOver ? "#f5f5f5" : "transparent",
											transition: "background-color 0.2s ease"
										}}
									>
										{temporaryValues.map((item, index) => (
											<Draggable key={item.code} draggableId={item.code} index={index}>
												{(provided, snapshot) => (
													<MDBox
														ref={provided.innerRef}
														{...provided.draggableProps}
														{...provided.dragHandleProps}
														display="flex"
														alignItems="center"
														bgcolor={snapshot.isDragging ? "primary.main" : "grey.100"}
														color={snapshot.isDragging ? "white" : "inherit"}
														borderRadius="4px"
														p={1}
														sx={{
															border: "1px solid #ccc",
															minWidth: "120px",
															cursor: "grab",
															"&:active": {
																cursor: "grabbing"
															},
															boxShadow: snapshot.isDragging
																? "0px 4px 8px rgba(0,0,0,0.2)"
																: "none",
															transform: snapshot.isDragging ? "rotate(5deg)" : "none",
															transition: "all 0.2s ease"
														}}
													>
														{/* Drag handle icon */}
														<Icon fontSize="small" sx={{ mr: 1, opacity: 0.6 }}>
															drag_indicator
														</Icon>

														{/* Label */}
														<MDBox flex={1} textAlign="center">
															<MDTypography variant="caption" fontWeight="medium">
																{item.label}
															</MDTypography>
														</MDBox>

														{/* Remove button */}
														<IconButton
															size="small"
															onClick={e => {
																e.stopPropagation();
																removeItem(index);
															}}
															sx={{ ml: 1 }}
														>
															<Icon fontSize="small" color="error">
																close
															</Icon>
														</IconButton>
													</MDBox>
												)}
											</Draggable>
										))}
										{provided.placeholder}

										{temporaryValues.length === 0 && (
											<MDBox
												display="flex"
												alignItems="center"
												justifyContent="center"
												width="100%"
												color="text.secondary"
											>
												<MDTypography variant="body2">
													{t("DATATABLE.noColumnsSelectedDescription")}
												</MDTypography>
											</MDBox>
										)}
									</MDBox>
								)}
							</Droppable>
						</DragDropContext>
					</MDBox>
				</MDBox>
			</DialogContent>
			<DialogActions>
				<MDButton variant="outlined" color="info" onClick={() => setReorderDialog({ open: false })}>
					{t("SETTINGS.cancel")}
				</MDButton>
				<MDButton
					variant="contained"
					color="info"
					onClick={() => {
						const newOrder = temporaryValues.map(item => item.code);
						onSave(newOrder);
					}}
				>
					{t("SETTINGS.save")}
				</MDButton>
			</DialogActions>
		</Dialog>
	);
};

export default ReorderColumnsDialog;
